import type { ContactInput, ResolvedQuestion } from '@aera/schema';
import { tidy } from '@aera/ui';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * I5: a separate, explicit consent for contact, distinct from the data-processing consent at the start.
 * The sentence agrees to contact "this way" (上面的方式), so the details given at I4 are shown above it.
 */
export function CheckboxQuestion({
  rq,
  checked,
  onChange,
  contact,
}: {
  rq: ResolvedQuestion;
  checked: boolean;
  onChange: (agree: boolean) => void;
  contact: ContactInput | null;
}) {
  const { t } = useTranslation();
  const means = [contact?.name, contact?.phoneOrWechat, contact?.email].map((s) => s?.trim()).filter(Boolean);
  return (
    <div className="space-y-4">
      {means.length > 0 ? (
        <div className="rounded-2xl bg-surface px-4 py-3">
          <p className="text-[13px] text-ink-2">{t('question.consentMeans')}</p>
          <p className="mt-1 break-words text-[15.5px]">{means.join(' · ')}</p>
        </div>
      ) : (
        <p className="rounded-2xl bg-surface px-4 py-3 text-[14px] text-ink-2">{t('question.consentNoMeans')}</p>
      )}
      <ConsentCheckbox id={`q-${rq.id}-box`} label={rq.prompt} checked={checked} onChange={onChange} />
    </div>
  );
}

export function ConsentCheckbox({
  id,
  label,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      id={id}
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="option-card flex w-full items-start gap-3 rounded-2xl border border-hair bg-white px-4 py-4 text-left text-[15.5px] leading-relaxed disabled:opacity-60"
    >
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors ${checked ? 'border-indigo bg-indigo text-white' : 'border-[#c9c9d1] bg-white'}`}
        aria-hidden
      >
        {checked && <Check className="lp-pop h-4 w-4" strokeWidth={3} />}
      </span>
      <span className="flex-1">{tidy(label)}</span>
    </button>
  );
}
